/**
 * The return journey, arriving.
 *
 * The callback is unauthenticated by necessity and is the hardest surface in the product, so the
 * screen that receives its answer assumes nothing: an unknown code still gets a title, a
 * sentence and a way forward. Nobody lands on a blank card and nobody reads a bare
 * `OAUTH_EXCHANGE_FAILED`.
 *
 * Four outcomes, three of which are screens of their own:
 *
 *   emailInUse   → `EmailInUseScreen`   — the account-linking refusal
 *   emailMissing → `MissingEmailScreen` — Facebook returned no address
 *   completing   → here, and only when the session did not open after all
 *   failed       → here, with copy keyed off the code
 *
 * The success path never reaches this file: the host card sees `status === 'authenticated'`
 * once `POST /auth/refresh` has exchanged the cookie the callback set, and navigates.
 */

import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'

import { useBranding } from '../../../lib/branding'
import { useT, useTNode } from '../../../lib/i18n'
import { Button } from '../../../components/ui/Button'
import { Icon } from '../../../components/ui/Icon'
import { AuthLayout } from '../AuthLayout'
import { EmailInUseScreen } from './EmailInUseScreen'
import { MissingEmailScreen } from './MissingEmailScreen'
import { ProviderButton } from './ProviderButton'
import { socialErrorCopy } from './copy'
import type { SocialCopyContext } from './copy'
import { isKnownSocialErrorCode, socialStartUrl } from './contract'
import type { SocialProvider, SocialReturn } from './contract'

export interface SocialReturnScreenProps {
  outcome: SocialReturn
  /** Where a fresh attempt should land. Already passed through `safeReturnTo`. */
  returnTo: string
  /** Drops the callback's query parameters and hands the card back to its form. */
  onUsePassword: () => void
}

export const SocialReturnScreen = ({
  outcome,
  returnTo,
  onUsePassword,
}: SocialReturnScreenProps): ReactElement => {
  const { branding } = useBranding()
  const t = useT()

  const ctx: SocialCopyContext = {
    provider: outcome.provider,
    productName: branding.productName,
    supportEmail: branding.supportEmail,
  }

  if (outcome.kind === 'emailInUse') {
    return <EmailInUseScreen ctx={ctx} email={outcome.email} onUsePassword={onUsePassword} />
  }

  if (outcome.kind === 'emailMissing') {
    return (
      <MissingEmailScreen
        ctx={ctx}
        provider={outcome.provider}
        state={outcome.state}
        returnTo={returnTo}
      />
    )
  }

  if (outcome.kind === 'completing') {
    return (
      <FailureCard
        title={t('auth.social.completingTitle')}
        body={t('auth.social.completingBody')}
        provider={outcome.provider}
        returnTo={returnTo}
        onUsePassword={onUsePassword}
        retryable
      />
    )
  }

  const copy = socialErrorCopy(t, outcome.code, ctx)

  return (
    <FailureCard
      title={copy.title}
      body={copy.body}
      provider={outcome.provider}
      returnTo={returnTo}
      onUsePassword={onUsePassword}
      retryable={copy.retryable}
      // A code we have no sentence for is still worth quoting to support — but only then, so a
      // recognised failure does not litter the card with jargon.
      code={isKnownSocialErrorCode(outcome.code) ? null : outcome.code}
    />
  )
}

interface FailureCardProps {
  title: string
  body: string
  provider: SocialProvider | null
  returnTo: string
  onUsePassword: () => void
  retryable: boolean
  code?: string | null
}

const FailureCard = ({
  title,
  body,
  provider,
  returnTo,
  onUsePassword,
  retryable,
  code = null,
}: FailureCardProps): ReactElement => {
  const t = useT()
  const tn = useTNode()

  return (
    <AuthLayout
      title={title}
      subtitle={body}
      footer={
        <p className="t-small text-ink-mute">
          {tn('auth.login.noAccount', {
            signup: (chunk) => (
              <Link
                to="/signup"
                className="ring-focus font-semibold text-brass-deep underline underline-offset-2"
              >
                {chunk}
              </Link>
            ),
          })}
        </p>
      }
    >
      <div className="flex flex-col gap-3">
        {retryable && provider ? (
          <ProviderButton
            provider={provider}
            onSelect={() => {
              window.location.assign(socialStartUrl(provider, returnTo))
            }}
          />
        ) : null}

        <Button
          variant={retryable && provider ? 'secondary' : 'primary'}
          size="lg"
          fullWidth
          onClick={onUsePassword}
        >
          {t('auth.social.usePassword')}
        </Button>

        {/* Not translated: this is the code the server sent, quoted for a support ticket. */}
        {code ? (
          <p className="t-mono flex items-center gap-1.5 text-ink-mute">
            <Icon name="info" size={14} />
            {code}
          </p>
        ) : null}
      </div>
    </AuthLayout>
  )
}
